// src/hooks/useAppTheme.js
//
// Per-application page themes (enhancements log, 18 Oct 2026). A product page
// can carry its own palette instead of the site's: while it is open the whole
// shell, header and footer included, reads that palette, and the moment the
// visitor leaves, the site theme is back.
//
// How it works: modernist.css defines the app palettes under
// :root[data-app-theme="<app>"][data-theme="dark" | "light"], swapping the
// same --color-* variables the site uses. This hook sets the two attributes.
// The app's own light/dark choice is kept per app for the visit
// (sessionStorage), so the site's toggle and remembered theme are untouched.
//
// Mount it AFTER useTheme in the same component: both write data-theme, and
// effects run in declaration order, so the app choice wins while an app is
// open and the site choice is restored (by this hook) when none is.
import { useCallback, useEffect, useState } from 'react';

export const APP_THEMES = {
  terminalx: { initial: 'dark' },
};

const key = (app) => `rw-app-theme.${app}`;

const read = (app) => {
  if (!app || !APP_THEMES[app]) return null;
  try {
    const saved = sessionStorage.getItem(key(app));
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (e) { /* private mode */ }
  return APP_THEMES[app].initial;
};

/**
 * @param {string|null} app   the open application's id, or null for the site
 * @param {'light'|'dark'} siteTheme   the site theme from useTheme
 * @returns [theme, toggle]  the theme in force and a toggle for it. With no
 *   app open, `theme` is the site theme and `toggle` is a no-op; use the
 *   site's own toggle for that.
 */
export default function useAppTheme(app, siteTheme) {
  const active = app && APP_THEMES[app] ? app : null;
  const [choices, setChoices] = useState({});
  const appTheme = active ? (choices[active] || read(active)) : null;
  const theme = active ? appTheme : siteTheme;

  useEffect(() => {
    const root = document.documentElement;
    if (active) {
      root.dataset.appTheme = active;
      root.dataset.theme = appTheme;
    } else {
      delete root.dataset.appTheme;
      root.dataset.theme = siteTheme;
    }
  }, [active, appTheme, siteTheme]);

  // Leaving the page for good must not leave the app palette behind.
  useEffect(() => () => { delete document.documentElement.dataset.appTheme; }, []);

  const toggle = useCallback(() => {
    if (!active) return;
    setChoices((c) => {
      const next = (c[active] || read(active)) === 'dark' ? 'light' : 'dark';
      try { sessionStorage.setItem(key(active), next); } catch (e) { /* private mode */ }
      return { ...c, [active]: next };
    });
  }, [active]);

  return [theme, toggle];
}
